/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import * as yup from 'yup'
import { WF_STEP_TYPE, WF_STEP_PROCESSING_TYPE } from './EditFormConstants'

const ValidationSchema = () => {
  yup.setLocale({
    mixed: {
      required: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
    }
  })

  const taskTeamStepLabel = yup
    .object()
    .shape({
      value: yup.number().nullable(),
      label: yup.string().nullable()
    })
    .nullable()
    .default(() => null)

  const someAtOnceErrorMessage = 'The # of users cannot be empty or set to 0, it must be set to 1 or more'
  const nofAllValue = yup
    .mixed()
    .when('processingType', {
      is: WF_STEP_PROCESSING_TYPE.N_OF_ALL,
      then: () =>
        yup
          .number()
          .min(1, someAtOnceErrorMessage)
          .nullable()
          .transform((value: any) => (isNaN(value) ? undefined : value))
          .required(someAtOnceErrorMessage)
    })
    .nullable()

  return yup.object().shape({
    name: yup
      .string()
      .trim()
      .max(255)
      .required()
      .default(() => ''),
    description: yup
      .string()
      .max(255)
      .nullable()
      .default(() => ''),
    id: yup.number().nullable(),
    initialApprovers: yup.object().shape({
      type: yup
        .string()
        .oneOf(Object.values(WF_STEP_TYPE))
        .default(() => WF_STEP_TYPE.NOT_DEFINED),
      steps: yup
        .array()
        .of(
          yup.object().shape({
            taskTeamStepLabel: taskTeamStepLabel,
            processingType: yup.string().nullable().required(),
            nofAllValue: nofAllValue,
            usersGroups: yup.array()
          })
        )
        .nullable()
        .default(() => [])
    }),
    collaborators: yup.object().shape({
      type: yup
        .string()
        .oneOf(Object.values(WF_STEP_TYPE))
        .default(() => WF_STEP_TYPE.USE_INITIATOR),
      steps: yup
        .array()
        .of(
          yup.object().shape({
            taskTeamStepLabel: taskTeamStepLabel,
            processingType: yup.string().nullable().required(),
            nofAllValue: nofAllValue,
            usersGroups: yup.array().required()
          })
        )
        .when('type', {
          is: WF_STEP_TYPE.USE_PREDEFINED,
          then: () => yup.array().required()
        })
        .nullable()
        .default(() => [])
    }),
    reviewers: yup.object().shape({
      type: yup
        .string()
        .oneOf(Object.values(WF_STEP_TYPE))
        .default(() => WF_STEP_TYPE.NOT_DEFINED),
      steps: yup
        .array()
        .of(
          yup.object().shape({
            taskTeamStepLabel: taskTeamStepLabel,
            processingType: yup.string().nullable().required(),
            nofAllValue: nofAllValue,
            usersGroups: yup.array().required()
          })
        )
        .when('type', {
          is: WF_STEP_TYPE.USE_PREDEFINED,
          then: () => yup.array().required()
        })
        .nullable()
        .default(() => [])
    }),
    approvers: yup.object().shape({
      type: yup
        .string()
        .oneOf(Object.values(WF_STEP_TYPE))
        .default(() => WF_STEP_TYPE.NOT_DEFINED),
      steps: yup
        .array()
        .of(
          yup.object().shape({
            taskTeamStepLabel: taskTeamStepLabel,
            processingType: yup.string().nullable().required(),
            nofAllValue: nofAllValue,
            usersGroups: yup.array().required()
          })
        )
        .when('type', {
          is: WF_STEP_TYPE.USE_PREDEFINED,
          then: () => yup.array().required()
        })
        .nullable()
        .default(() => []),
      approveActions: yup
        .array()
        .nullable()
        .default(() => [])
    }),
    rejectionReviewers: yup.object().shape({
      type: yup
        .string()
        .oneOf(Object.values(WF_STEP_TYPE))
        .default(() => WF_STEP_TYPE.USE_INITIATOR),
      usersGroups: yup
        .array()
        .nullable()
        .default(() => [])
    }),
    trainees: yup.object().shape({
      createRAActionType: yup
        .string()
        .oneOf(
          Object.values({
            FOR_NOBODY: 'FOR_NOBODY',
            FOR_NEW_USERS_ONLY: 'FOR_NEW_USERS_ONLY',
            FOR_ALL_USERS: 'FOR_ALL_USERS'
          })
        )
        .default(() => 'FOR_NOBODY'),
      usersGroups: yup
        .array()
        .nullable()
        .default(() => [])
    })
  })
}

export default ValidationSchema
